import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircle, Users, BarChart3, Shield, Zap,
  Monitor, ArrowRight, Star, Mail
} from 'lucide-react';
import '../styles/Pages.css';
import '../styles/Demo.css';
import { DemoHero } from '../components/PageHeroes';
import { sendEmail, ENDPOINTS } from '../utils/sendEmail';

const Demo = () => {
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', company: '',
    phone: '', employees: '', message: ''
  });
  const [status, setStatus] = useState('idle');

  const highlights = [
    { icon: BarChart3, title: 'Live Financial Dashboard', description: 'See real-time payroll, invoicing, and accounting data in one dashboard.', accent: 'blue' },
    { icon: Shield,    title: 'HMRC RTI & VAT Filing',      description: 'Watch a full RTI payroll submission and a VAT return, start to confirmation.', accent: 'green' },
    { icon: Users,     title: 'Team Management',            description: 'Explore multi-user roles, permissions, employee records, and approval workflows.', accent: 'blue' },
    { icon: Zap,       title: 'Automation in Action',       description: 'See payslips, appointment reminders, and auto-enrolment run automatically.', accent: 'green' }
  ];

  const steps = [
    { num: '01', title: 'Fill in your details',     desc: 'Tell us about your company so we can tailor the demo.' },
    { num: '02', title: 'We confirm within 1 hour', desc: 'A specialist will reach out to schedule your session.' },
    { num: '03', title: 'Join your live demo',       desc: 'A 30-minute walkthrough built around your use case.' }
  ];

  const handleChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await sendEmail(ENDPOINTS.DEMO, {
        name:    `${form.firstName} ${form.lastName}`,
        firstName:   form.firstName,
        lastName:    form.lastName,
        email:        form.email,
        company:      form.company,
        phone:        form.phone || 'Not provided',
        employees:    form.employees,
        message:      form.message || 'No specific requirements noted.',
        to_email:     'thomas@effectivestaff.co.uk',
        reply_to:     form.email,
      });
      setStatus('success');
      setForm({ firstName: '', lastName: '', email: '', company: '', phone: '', employees: '', message: '' });
    } catch (err) {
      console.error('EmailJS error:', err);
      setStatus('error');
    }
  };

  const containerVariants = {
    hidden:  { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
  };
  const itemVariants = {
    hidden:  { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } }
  };

  return (
    <motion.div className="demo-page" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}>
      <DemoHero />

      {/* ===== WHAT YOU'LL SEE ===== */}
      <section className="demo-section">
        <div className="demo-container">
          <motion.div className="demo-section-header" initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <span className="demo-eyebrow-sm">What's covered</span>
            <h2>What you'll see in your demo</h2>
            <p>We walk you through the features that matter most for your business — payroll, finance, or both.</p>
          </motion.div>
          <motion.div className="demo-highlights-grid" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            {highlights.map((item, idx) => {
              const Icon = item.icon;
              return (
                <motion.div key={idx} className={`demo-highlight-card accent-${item.accent}`} variants={itemVariants} whileHover={{ y: -6 }}>
                  <div className="demo-hl-icon">
                    <Icon size={22} />
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* ===== HOW IT WORKS + FORM ===== */}
      <section className="demo-section demo-section-alt">
        <div className="demo-container">
          <div className="demo-two-col">

            {/* Steps */}
            <motion.div className="demo-steps-col" initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
              <span className="demo-eyebrow-sm">The process</span>
              <h2>How it works</h2>
              <p className="demo-steps-intro">From request to live demo in under 24 hours.</p>
              <div className="demo-steps">
                {steps.map((step, idx) => (
                  <div key={idx} className="demo-step">
                    <div className="demo-step-num">{step.num}</div>
                    <div>
                      <h4>{step.title}</h4>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="demo-trust">
                <Star size={15} className="demo-trust-star" fill="currentColor" />
                <span>Trusted by 500+ UK businesses</span>
              </div>
            </motion.div>

            {/* Form */}
            <motion.div className="demo-form-col" initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.08 }}>
              <div className="demo-form-card">
                <div className="demo-form-header">
                  <Monitor size={20} className="demo-form-header-icon" />
                  <h3>Request your free demo</h3>
                </div>

                {status === 'success' ? (
                  <div className="demo-success">
                    <CheckCircle size={44} className="demo-success-icon" />
                    <h3>Request received!</h3>
                    <p>A specialist will reach out within 1 hour to confirm your session.</p>
                  </div>
                ) : (
                  <form className="demo-form" onSubmit={handleSubmit}>
                    <div className="form-row">
                      <div className="form-group">
                        <label>First name *</label>
                        <input name="firstName" value={form.firstName} onChange={handleChange} placeholder="Jane" required />
                      </div>
                      <div className="form-group">
                        <label>Last name *</label>
                        <input name="lastName" value={form.lastName} onChange={handleChange} placeholder="Smith" required />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Work email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} placeholder="jane@company.co.uk" required />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Company name *</label>
                        <input name="company" value={form.company} onChange={handleChange} placeholder="Acme Ltd" required />
                      </div>
                      <div className="form-group">
                        <label>Phone number</label>
                        <input name="phone" value={form.phone} onChange={handleChange} placeholder="+44 7700 900000" />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Number of employees *</label>
                      <select name="employees" value={form.employees} onChange={handleChange} required>
                        <option value="">Select range</option>
                        <option value="1-10">1 - 10</option>
                        <option value="11-50">11 - 50</option>
                        <option value="51-200">51 - 200</option>
                        <option value="201-500">201 - 500</option>
                        <option value="500+">500+</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Anything specific you would like covered?</label>
                      <textarea name="message" value={form.message} onChange={handleChange} placeholder="e.g. CIS payroll, invoicing, VAT returns, pension auto-enrolment, multi-company setup..." rows={3} />
                    </div>
                    {status === 'error' && (
                      <p className="form-error">Something went wrong. Please try again or email us directly.</p>
                    )}
                    <button type="submit" className="demo-submit-btn" disabled={status === 'sending'}>
                      {status === 'sending' ? 'Sending...' : (
                        <span className="demo-submit-content">
                          Request demo <ArrowRight size={15} />
                        </span>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="demo-cta-section">
        <div className="demo-cta-bg" />
        <div className="demo-container">
          <motion.div className="demo-cta-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <div className="demo-cta-icon"><Mail size={26} /></div>
            <h2>Prefer to reach out directly?</h2>
            <p>Our team is happy to answer questions before the demo.</p>
            <a href="mailto:thomas@effectivestaff.co.uk" className="demo-cta-btn">Email us</a>
          </motion.div>
        </div>
      </section>
    </motion.div>
  );
};

export default Demo;